import type { CSSProperties } from 'react';
import type { howItWorksSection } from '../../../../app/_shared/marketing/landingContent';
import { withEmphasis } from './emphasisText';
import './problemAndWhy.css';

type Step = (typeof howItWorksSection.steps)[number];
type Night = (typeof howItWorksSection.nights)[number];

type Props = {
  eyebrow: string;
  h2: string;
  lead: string;
  steps: readonly Step[];
  nights: readonly Night[];
  noPreference: string;
};

/**
 * Replaces `DropRhythmBand` ("Every evening at 7pm, Circles form") at #how. The product asks a
 * visitor to pick ONE of four weekly nights (or no preference) rather than join whichever nightly
 * drop fills next, so this is the mechanism explainer for that story, not a schedule reminder.
 * Reuses the `.stmt-section` / `.stmt-item` statement block (see problemAndWhy.css) with a
 * `stmt-grid--four` modifier for the extra step.
 */
export function HowItWorksBand({ eyebrow, h2, lead, steps, nights, noPreference }: Props) {
  return (
    <section className="stmt-section" aria-labelledby="how-it-works-heading">
      <header className="stmt-section__head">
        <p className="stmt-section__eyebrow">{eyebrow}</p>
        <h2
          id="how-it-works-heading"
          className="ml-heading-serif ml-text-heading stmt-section__title">
          {h2}
        </h2>
        <p className="stmt-section__lead">{lead}</p>
      </header>

      <div className="stmt-grid stmt-grid--four">
        {steps.map((step, index) => (
          <article
            key={step.id}
            className="stmt-item ml-revealStaggerItem"
            style={{ '--reveal-i': index } as CSSProperties}>
            <h3 className="ml-heading-sans stmt-item__title">{step.title}</h3>
            <p className="stmt-item__body">{withEmphasis(step.body)}</p>
          </article>
        ))}
      </div>

      <ul className="how-it-works__nights" aria-label="The four weekly nights">
        {nights.map((night) => (
          <li key={night.day} className="how-it-works__night">
            <span className="how-it-works__nightDay">{night.day}</span>
            <span className="how-it-works__nightLabel">{night.label}</span>
            <span className="how-it-works__nightTime">{night.time}</span>
          </li>
        ))}
      </ul>
      <p className="how-it-works__noPreference">{noPreference}</p>
    </section>
  );
}
